import { forwardRef, useContext, useRef } from "react";
import "./ReservationSchedulerNew.css";
import * as Hi from "react-icons/hi";
import * as Io from "react-icons/io";
import { AppContext } from "../../../context/AppContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  MdEventBusy,
  MdLockOpen,
  MdChevronRight,
} from "react-icons/md";

// Helpers
const prettyDate = (iso, compact = false) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, compact
    ? { weekday: "short", day: "2-digit", month: "short", year: "numeric" }
    : { weekday: "long", day: "2-digit", month: "short", year: "numeric" }
  );
};

// TZ-safe day math
const toDayIdx = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
};
const fromDayIdx = (idx) => {
  const dt = new Date(idx * 86400000);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const addDays = (iso, delta) => fromDayIdx(toDayIdx(iso) + delta);

function DateNavBar({
  selectedDate,
  setSelectedDate,
  count = 0,
  holidayIntervals,
  setShowModal,
  isClosed,
  setIsClosed,
  setShowClosureModal,
}) {
  const {
    fetchNewReservations,
    fetchReservationHistory,
    fetchFutureReservations,
    upcomingwithtodaysReservationList,
    fetchAllTables,
    reservationLoading,
    upComingHolidays,
  } = useContext(AppContext);
  const dateRef = useRef(null);
  const ref = useRef(null);
  const datePickerRef = useRef(null);

  const handleMouseDown = (e) => {
    e.preventDefault();

    if (ref.current?.showPicker) {
      ref.current.showPicker();
    } else {
      ref.current?.focus();
      ref.current?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    }
  };

  const openCalendar = () => {
    const el = dateRef.current;
    if (!el) return;

    try {
      el.focus({ preventScroll: true });
    } catch (_) {}

    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        requestAnimationFrame(() => {
          try {
            el.focus({ preventScroll: true });
            el.showPicker();
          } catch {
            // final fallback
            el.click();
          }
        });
        return;
      }
    }

    try {
      el.click();
    } catch {
      el.focus();
    }
  };

  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const min = getTodayString();
  const isPrevDisabled = !selectedDate || selectedDate <= min;

  const fetchAllBookinGdetails = async () => {
    const shopToken = window.localStorage.getItem("shopCustomToken");

    if (!shopToken) return;

    await fetchNewReservations(shopToken);
    await fetchReservationHistory(shopToken);
    await fetchFutureReservations(shopToken);
    await upcomingwithtodaysReservationList(shopToken);
    await fetchAllTables(shopToken);
  };

  // Red center date button (matches mobile design)
  const NavDateInput = forwardRef(
    ({ value, onClick, onKeyDown, disabled }, inputRef) => {
      return (
        <button
          type="button"
          ref={inputRef}
          onClick={onClick}
          onKeyDown={onKeyDown}
          disabled={disabled}
          className="rsdbar__dateBtn"
          aria-label="Pick date"
        >
          <Io.IoMdCalendar className="rsdbar__dateBtn-icon" aria-hidden />
          <span className="rsdbar__dateBtn-text">
            {prettyDate(selectedDate, true) || value || "Select date"}
          </span>
        </button>
      );
    }
  );

  return (
    <div className="rsdbar rsdbar--right">
      {/* Hidden native date input */}
      {/* <input
        ref={dateRef}
        type="date"
        className="rsdbar__dateinput--visuallyHidden"
        value={selectedDate}
        onChange={(e) => setSelectedDate(e.target.value)}
        min={min}
      /> */}

      {/* Date navigation: prev | red date | next */}
      <div className="rsdbar__row rsdbar__row--nav">
        <div className="rsdbar__info">
          <div className="rsdbar__btns position-relative">
            <button
              type="button"
              className="rsdbar__navbtn"
              aria-label="Previous day"
              onClick={() => setSelectedDate((d) => addDays(d, -1))}
              title="Previous day"
              disabled={isPrevDisabled}
            >
              <Hi.HiArrowLeft />
            </button>
            {/* <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            name=""
            id=""
            ref={ref}
            className="rsdbar__navbtn rsdbar__navbtn--icon"
            inputMode="none"
            style={{
              userSelect: "none",
              caretColor: "transparent",
              cursor: "pointer",
            }}
            onMouseDown={handleMouseDown}
            min={min}
          /> */}

            <DatePicker
              selected={selectedDate ? new Date(selectedDate) : null}
              onChange={(date) => {
                if (!date) {
                  setSelectedDate("");
                  return;
                }
                const iso = date.toISOString().split("T")[0];
                setSelectedDate(iso);
              }}
              minDate={min}
              // excludeDates={disabledDates}
              dateFormat="dd-MM-yyyy"
              customInput={<NavDateInput />}
              // showIcon
              // icon={<Io.IoMdCalendar />}
              // excludeDateIntervals={holidayIntervals}
              popperPlacement="bottom"
              popperClassName="rsdbar__datepicker-popper"
            />
            <button
              type="button"
              className="rsdbar__navbtn"
              aria-label="Next day"
              onClick={() => setSelectedDate((d) => addDays(d, +1))}
              title="Next day"
            >
              <Hi.HiArrowRight />
            </button>
          </div>
        </div>
      </div>

      {/* Meta: bookings count + refresh + manage closure */}
      <div className="rsdbar__row rsdbar__row--meta">
        <div className="rsdbar__countText">
          {count} booking{count === 1 ? "" : "s"}
        </div>

        {/* Previous large date text (kept for reference)
        <div className="rsdbar__dateText">
          <span className="rsdbar__dateText--full">{prettyDate(selectedDate)}</span>
          <span className="rsdbar__dateText--compact">
            {prettyDate(selectedDate, true)}
          </span>
        </div>
        */}

        <div className="rsdbar__metaActions">
          <button
            type="button"
            className={
              !reservationLoading
                ? "rsdbar__iconBtn refresh_btn"
                : "rsdbar__iconBtn refresh_btn rotate"
            }
            disabled={reservationLoading}
            onClick={fetchAllBookinGdetails}
            aria-label="Refresh"
            title="Refresh"
          >
            <Io.IoMdRefresh />
            <span className="rsdbar__refresh-label">Refresh</span>
          </button>

          <div className="rs-topbar__right">
            <button
              type="button"
              className={
                "rs-closeBtn " +
                (isClosed ? "rs-closeBtn--reopen" : "rs-closeBtn--close")
              }
              onClick={() => {
                setShowClosureModal(true);
              }}
              title="Temporarily stop accepting reservations for this date."
              aria-label="Manage Closure"
            >
              {isClosed ? (
                <>
                  <MdLockOpen />
                  <span className="rs-closeBtn__label">Manage Closure</span>
                </>
              ) : (
                <>
                  <MdEventBusy className="rs-closeBtn__icon" />
                  <span className="rs-closeBtn__label">Manage Closure</span>
                  <MdChevronRight className="rs-closeBtn__chevron" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DateNavBar;
